import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Badge,
  Chip,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { Star, Eye, EyeOff } from 'lucide-react';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { industryLabel, sourceLabel } from './label-maps';
import { humanize } from './status-tones';
import {
  useCustomerContacts,
  useContactAccountStatus,
  useContactWhois,
  useSetPrimaryContact,
  useDeleteContact,
  useSyncContactAccount,
  useEnableContactAccount,
  useDisableContactAccount,
  useUnlinkContactAccount,
  useImpersonateContact,
  type CustomerContact,
  type ContactDeleteAuthAction,
} from '@/api/customer-basic';
import { ContactActionsMenu } from './contact-actions-menu';
import { ContactEditDialog } from './contact-edit-dialog';
import { CreateAccountDialog } from './create-account-dialog';

/** Mask an email, keeping the first char and the domain: `a•••@gmail.com`. */
function maskEmail(email?: string): string {
  if (!email) return '—';
  const [user, domain] = email.split('@');
  if (!domain) return `${email.charAt(0)}•••`;
  return `${user.charAt(0)}•••@${domain}`;
}

/** Mask a phone, keeping the first 3 and last 3 digits: `098••••580`. */
function maskPhone(phone?: string): string {
  if (!phone) return '—';
  if (phone.length <= 6) return `${phone.charAt(0)}••••`;
  return `${phone.slice(0, 3)}••••${phone.slice(-3)}`;
}

/** Contact status → StatusPill tone. */
function contactTone(status?: string): 'success' | 'neutral' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'inactive' || status === 'blocked') return 'danger';
  return 'neutral';
}

/** A single reveal-on-demand masked value (email / phone). */
function MaskedValue({ full, masked }: { full: string; masked: string }) {
  const [shown, setShown] = useState(false);
  if (!full || full === '—') return <span>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
      <span>{shown ? full : masked}</span>
      <Button
        variant="ghost"
        size="sm"
        startIcon={shown ? <EyeOff size={14} /> : <Eye size={14} />}
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? 'Hide' : 'Reveal'}
        style={{ padding: '0 var(--vn-space-1)' }}
      >
        {shown ? 'Hide' : 'Reveal'}
      </Button>
    </span>
  );
}

/** Chip row helper for role / label / interest lists. */
function ChipList({ values, tone }: { values: string[]; tone?: 'info' }) {
  if (!values.length) return null;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
      {values.map((v) => (
        <Chip key={v} size="sm" tone={tone} appearance={tone ? 'subtle' : undefined}>
          {humanize(v)}
        </Chip>
      ))}
    </span>
  );
}

function contactFacts(contact: CustomerContact): InfoItem[] {
  return [
    { label: 'Email', value: <MaskedValue full={contact.contact_email} masked={maskEmail(contact.contact_email)} /> },
    { label: 'Phone', value: <MaskedValue full={contact.contact_phone} masked={maskPhone(contact.contact_phone)} /> },
    { label: 'Position', value: contact.contact_position },
    { label: 'Industry', value: industryLabel(contact.contact_industry) },
    { label: 'Source', value: sourceLabel(contact.contact_source) },
    { label: 'Language', value: contact.contact_language ? humanize(contact.contact_language) : undefined },
    { label: 'Status', value: <StatusPill tone={contactTone(contact.contact_status)}>{humanize(contact.contact_status ?? 'unknown')}</StatusPill> },
    { label: 'Roles', value: <ChipList values={contact.contact_roles ?? []} /> },
    { label: 'Labels', value: <ChipList values={contact.contact_label ?? []} /> },
    { label: 'Interested Services', value: <ChipList values={contact.contact_service_interestings ?? []} tone="info" />, full: true },
    { label: 'Note', value: contact.contact_note, full: true },
  ];
}

/** Delete-contact confirm with a required message + how to treat a linked account. */
function DeleteContactConfirm({
  open,
  onOpenChange,
  customerId,
  contact,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  customerId: number;
  contact: CustomerContact;
}) {
  const [message, setMessage] = useState('');
  const [authAction, setAuthAction] = useState<ContactDeleteAuthAction>('keep');
  const del = useDeleteContact(customerId);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setMessage('');
          setAuthAction('keep');
        }
        onOpenChange(o);
      }}
      title="Delete contact"
      description={`Remove "${contact.contact_name || contact.contact_email}"? This cannot be undone.`}
      destructive
      confirmLabel="Delete"
      loading={del.isPending}
      onConfirm={() =>
        del.mutate(
          { contactId: contact.customer_contact_id, payload: { message, auth_action: authAction } },
          { onSuccess: () => onOpenChange(false) },
        )
      }
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Field label="Reason" description="Recorded on the audit log.">
          {(p) => <Input {...p} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Why is this contact being removed?" />}
        </Field>
        <Field label="Linked account" description="How to treat a Keycloak account bound to this contact.">
          {(p) => (
            <Select value={authAction} onValueChange={(v) => setAuthAction(v as ContactDeleteAuthAction)}>
              <SelectTrigger id={p.id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="keep">Keep the account</SelectItem>
                <SelectItem value="unlink">Unlink the account</SelectItem>
                <SelectItem value="delete">Delete the account</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>
    </ConfirmDialog>
  );
}

/** A single contact card + its per-contact account state and management actions. */
function ContactCard({ customerId, contact }: { customerId: number; contact: CustomerContact }) {
  const contactId = contact.customer_contact_id;
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  const status = useContactAccountStatus(contactId);
  const whois = useContactWhois(contactId);
  const setPrimary = useSetPrimaryContact(customerId);
  const sync = useSyncContactAccount(contactId);
  const enable = useEnableContactAccount(contactId);
  const disable = useDisableContactAccount(contactId);
  const unlink = useUnlinkContactAccount(contactId);
  const impersonate = useImpersonateContact(contactId);

  const partnerUrl = import.meta.env.APP_PARTNER_URL as string | undefined;
  const canImpersonate = !!whois.data?.valid && !!partnerUrl;
  const hasKeycloakAccount = status.data?.authentication_method === 'keycloak';
  const hasNoAccount = status.isSuccess && !status.data?.has_account;
  const isAccountEnabled = status.data?.keycloak_status?.enabled ?? false;
  const accountBusy = sync.isPending || enable.isPending || disable.isPending || unlink.isPending;

  const account = status.isSuccess
    ? {
        hasKeycloakAccount,
        hasNoAccount,
        isAccountEnabled,
        onCreateAccount: () => setCreatingAccount(true),
        onSync: () => sync.mutate(),
        onEnable: () => enable.mutate(undefined),
        onDisable: () => disable.mutate(undefined),
        onUnlink: () => unlink.mutate(),
        onImpersonate: canImpersonate ? () => impersonate.mutate() : undefined,
      }
    : undefined;

  return (
    <Card flat>
      <CardHeader
        actions={
          <ContactActionsMenu
            isPrimary={!!contact.contact_firstable}
            onEdit={() => setEditing(true)}
            onSetPrimary={() => setPrimary.mutate(contactId)}
            onDelete={() => setDeleting(true)}
            account={account}
            busy={accountBusy}
          />
        }
      >
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span>{contact.contact_name || 'Unnamed contact'}</span>
          {contact.contact_firstable ? (
            <Badge tone="warning" appearance="subtle">
              <Star size={12} style={{ marginRight: 4 }} />
              Primary
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardBody>
        {account && hasKeycloakAccount && !isAccountEnabled ? (
          <Alert tone="warning" title="This contact's account is currently disabled." style={{ marginBottom: 'var(--vn-space-4)' }} />
        ) : null}
        <InfoGrid items={contactFacts(contact)} />
      </CardBody>

      <ContactEditDialog open={editing} onOpenChange={setEditing} customerId={customerId} contact={contact} />
      <CreateAccountDialog
        open={creatingAccount}
        onOpenChange={setCreatingAccount}
        contactId={contactId}
        contactEmail={contact.contact_email}
      />
      <DeleteContactConfirm open={deleting} onOpenChange={setDeleting} customerId={customerId} contact={contact} />
    </Card>
  );
}

/**
 * Customer contact section — lists the customer's contacts (primary starred),
 * each with masked email/phone (per-value Reveal) and a "⋯" actions menu wiring
 * edit / set-primary / delete plus the Keycloak account lifecycle. Endpoints and
 * field names mirror datacentral-client-v4.
 */
export function CustomerContactSection({ customerId }: { customerId: number }) {
  const { data, isLoading, isError } = useCustomerContacts(customerId);
  const contacts = data?.results ?? [];
  const count = data?.pagination?.total ?? contacts.length;

  // Show one contact at a time, defaulting to the primary (legacy behaviour) —
  // scales better than stacking every card when a customer has many contacts.
  const [picked, setPicked] = useState<number | null>(null);
  const defaultId =
    contacts.find((c) => c.contact_firstable)?.customer_contact_id ?? contacts[0]?.customer_contact_id;
  const selectedId = picked ?? defaultId;
  const selected = contacts.find((c) => c.customer_contact_id === selectedId) ?? contacts[0];

  return (
    <Card flat>
      <CardHeader actions={count > 0 ? <Badge tone="info" appearance="subtle">{count}</Badge> : undefined}>
        <CardTitle>Contacts</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton variant="text" width="50%" />
        ) : isError ? (
          <Alert tone="danger" title="Failed to load contacts." />
        ) : contacts.length === 0 ? (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No contacts yet.</span>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {contacts.length > 1 ? (
              <Field label="Select contact">
                {(p) => (
                  <Select value={String(selectedId)} onValueChange={(v) => setPicked(Number(v))}>
                    <SelectTrigger id={p.id} style={{ maxWidth: 440 }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contacts.map((c) => (
                        <SelectItem key={c.customer_contact_id} value={String(c.customer_contact_id)}>
                          {(c.contact_name || c.contact_email || `Contact #${c.customer_contact_id}`) +
                            (c.contact_firstable ? ' — Primary' : '')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            ) : null}
            {selected ? (
              <ContactCard key={selected.customer_contact_id} customerId={customerId} contact={selected} />
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default CustomerContactSection;
